<script setup lang="ts">
import { nextTick, ref, type ComponentPublicInstance } from 'vue'

import type { InsightResult } from '../../models/insight.types'
import type { InsightOutcomeGroup } from '../../service/insightPresentation'

import InsightCard from './InsightCard.vue'
import InsightOutcomeIcon from './InsightOutcomeIcon.vue'

import { useI18n } from '@/shared/i18n'
import { cur, useFormat } from '@/shared/lib/format'
import { Icon } from '@/shared/ui/icon'

const props = withDefaults(
  defineProps<{
    groups: readonly InsightOutcomeGroup[]
    currency?: string | undefined
  }>(),
  {},
)
const emit = defineEmits<{ open: [insight: InsightResult] }>()
const { t } = useI18n()
const SEVERITY_TEXT_CLASS: Record<string, string> = {
  danger: 'text-[var(--ui-color-insight-attention)]',
  success: 'text-[var(--ui-color-insight-opportunity)]',
  info: 'text-info',
}
const format = props.currency ? useFormat({ currency: props.currency }) : useFormat()
const openOutcomes = ref(new Set<string>())
const groupElements = new Map<string, HTMLElement>()

function isOpen(key: string): boolean {
  return openOutcomes.value.has(key)
}

function toggleOutcome(key: string): void {
  const next = new Set(openOutcomes.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  openOutcomes.value = next
}

async function openOutcome(key: string): Promise<void> {
  openOutcomes.value = new Set([...openOutcomes.value, key])
  await nextTick()
  groupElements.get(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function setGroupElement(key: string, element: Element | ComponentPublicInstance | null): void {
  if (element instanceof HTMLElement) groupElements.set(key, element)
  else groupElements.delete(key)
}

function formatImpact(value: number): string {
  const amount = format.formatNumber(value, { maxFrac: 0, heavyDefault: true })
  if (!props.currency) return format.formatCurrency(value, { maxFrac: 0 })
  return `${amount}${cur.resolveCurrencySymbol(props.currency, format.locale)}`
}

defineExpose({ openOutcome })
</script>

<template>
  <div class="insight-list grid gap-md">
    <section
      v-for="group in groups"
      :id="`insight-outcome-${group.key}`"
      :key="group.key"
      :ref="(element) => setGroupElement(group.key, element)"
      class="insight-list__group scroll-mt-18 overflow-hidden rounded-md bg-surface shadow-card"
      :data-testid="`insight-outcome-${group.key}`"
    >
      <button
        type="button"
        class="insight-list__group-header sticky top-0 z-1 grid w-full min-h-24 grid-cols-[minmax(0,_1fr)_minmax(12rem,_16rem)_1.25rem] items-center gap-md p-[1.5rem_1.75rem] max-[26rem]:p-md border border-transparent rounded-[inherit] bg-surface text-fg [font:inherit] text-start cursor-pointer [&:hover]:border-ring [&:hover]:outline-none [&:focus-visible]:border-ring [&:focus-visible]:outline-none max-[42rem]:static max-[42rem]:grid-cols-[minmax(0,_1fr)_1rem]"
        :data-testid="`insight-outcome-toggle-${group.key}`"
        :aria-expanded="isOpen(group.key)"
        :aria-controls="`insight-outcome-panel-${group.key}`"
        @click="toggleOutcome(group.key)"
      >
        <span class="insight-list__identity flex items-start min-w-0 gap-sm">
          <InsightOutcomeIcon
            class="insight-list__outcome-icon"
            :outcome="group.key"
            :icon="group.icon"
            :icon-weight="group.iconWeight"
          />
          <span>
            <strong>{{ t(group.labelKey) }}</strong>
            <small>{{ t(group.descriptionKey) }}</small>
          </span>
        </span>
        <span
          class="insight-list__totals flex items-start flex-col gap-2 font-numeric max-[42rem]:col-span-full"
        >
          <strong>{{ formatImpact(group.totalImpact) }}</strong>
          <span
            class="insight-list__severity-summary flex items-center flex-wrap gap-sm font-sans text-[0.75rem]"
          >
            <span
              v-for="item in group.buckets"
              :key="item.key"
              :class="SEVERITY_TEXT_CLASS[item.tone] ?? 'text-muted'"
            >
              <span
                :class="`insight-list__dot--${item.tone}`"
                class="insight-list__dot w-[0.45rem] h-[0.45rem] rounded-full bg-muted"
              />
              {{ format.formatNumber(item.items.length) }} {{ t(item.labelKey) }}
            </span>
          </span>
        </span>
        <Icon
          name="chevron-down"
          :size="16"
          class="insight-list__chevron text-muted transition-[transform] duration-150 ease-[ease] max-[42rem]:col-2 max-[42rem]:row-1"
          :class="{ 'insight-list__chevron--open': isOpen(group.key) }"
        />
      </button>

      <div
        v-if="isOpen(group.key)"
        :id="`insight-outcome-panel-${group.key}`"
        class="insight-list__panel grid gap-md pt-0 px-4 pb-4"
      >
        <section
          v-for="bucket in group.buckets"
          :key="bucket.key"
          class="insight-list__bucket grid gap-sm"
        >
          <header
            class="insight-list__bucket-header flex items-center w-fit gap-xs p-[0.2rem_0.6rem] rounded-sm text-muted bg-surface-sunken text-[0.875rem] leading-5"
            :class="`insight-list__bucket-header--${bucket.tone}`"
          >
            <Icon :name="bucket.icon" :size="16" />
            <strong class="font-medium">{{ t(bucket.labelKey) }}</strong>
            <span class="px-[0.35rem] rounded-sm bg-current/10 text-[0.7rem] leading-4">{{
              format.formatNumber(bucket.items.length)
            }}</span>
          </header>
          <div
            class="insight-list__cards grid grid-cols-3 gap-md max-[42rem]:grid-cols-[minmax(0,_1fr)]"
            :class="{
              'insight-list__cards--single': bucket.items.length === 1,
              'insight-list__cards--double': bucket.items.length === 2,
            }"
          >
            <InsightCard
              v-for="insight in bucket.items"
              :key="insight.id"
              :insight="insight"
              :currency="currency"
              @open="emit('open', $event)"
            />
          </div>
        </section>
      </div>
    </section>
  </div>
</template>

<style scoped>
.insight-list .insight-list__severity-summary span {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}
.insight-list .insight-list__identity > span:last-child {
  display: grid;
  min-width: 0;
  gap: 0.4rem;
}
.insight-list .insight-list__identity strong {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.75rem;
}
.insight-list .insight-list__identity small {
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  line-height: 1rem;
}
.insight-list .insight-list__totals > strong {
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 2rem;
}
.insight-list :deep(.insight-list__dot--danger) {
  background-color: var(--ui-color-insight-attention);
}
.insight-list :deep(.insight-list__dot--success) {
  background-color: var(--ui-color-insight-opportunity);
}
.insight-list :deep(.insight-list__dot--info) {
  background-color: var(--ui-color-info);
}
.insight-list .insight-list__chevron--open {
  transform: rotate(180deg);
}
.insight-list :deep(.insight-list__bucket-header--danger) {
  color: var(--ui-color-danger);
  background-color: var(--ui-color-danger-soft);
}
.insight-list :deep(.insight-list__bucket-header--success) {
  color: var(--ui-color-success);
  background-color: var(--ui-color-success-soft);
}
.insight-list :deep(.insight-list__bucket-header--info) {
  color: var(--ui-color-info);
  background-color: var(--ui-color-info-soft);
}
.insight-list .insight-list__cards--single {
  grid-template-columns: minmax(0, 1fr);
}
.insight-list .insight-list__cards--double {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (width < 68rem) {
  .insight-list__cards:not(.insight-list__cards--single) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
